import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLeaves, updateLeaves } from '@/api/leave';
import { getUsers } from '@/api/user';
import TableCustomer from '@/components/table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, User, Filter } from 'lucide-react';

const Leaves: React.FC = () => {
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState<string>('all');
    const [month, setMonth] = useState<string>('all');
    const [userId, setUserId] = useState<string>('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<'Approved' | 'Rejected'>('Approved');
    const [adminNote, setAdminNote] = useState('');
    const queryClient = useQueryClient();

    const { data: usersData } = useQuery({
        queryKey: ['users', 'statistics'],
        queryFn: () => getUsers({ page: 1, limit: 100 }),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['leaves', page, status, month, userId],
        queryFn: () => getLeaves({
            page,
            limit: 10,
            status: status === 'all' ? undefined : status,
            month: month === 'all' ? undefined : month,
            uid: userId === 'all' ? undefined : userId
        }),
    });

    const updateLeaveMutation = useMutation({
        mutationFn: updateLeaves,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leaves'] });
            toast.success('Update status successfully');
            setIsModalOpen(false);
            setAdminNote('');
            setSelectedLeave(null);
            setSelectedStatus('Approved');
        },
        onError: () => {
            toast.error('Failed to update status');
        },
    });

    const handleOpenModal = (id: string) => {
        setSelectedLeave(id);
        setIsModalOpen(true);
    };

    const handleConfirmUpdate = () => {
        if (selectedLeave) {
            updateLeaveMutation.mutate({
                id: selectedLeave,
                status: selectedStatus,
                adminNote: adminNote.trim()
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Approved':
                return 'bg-green-100 text-green-800 hover:bg-green-200';
            case 'Rejected':
                return 'bg-red-100 text-red-800 hover:bg-red-200';
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
            default:
                return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'Approved':
                return 'Approved';
            case 'Rejected':
                return 'Rejected';
            case 'Pending':
                return 'Pending';
            default:
                return 'Unknown';
        }
    };

    const columns = [
        {
            label: 'Employee Name',
            value: 'user.name',
            render: (data: any) => {
                console.log(data);
                return (
                    <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>{data.data.user.name}</span>
                    </div>
                )
            }
        },
        {
            label: 'Start Date',
            value: 'leave.startDate',
            isTime: true,
            render: (data: any) => (
                <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <span>{format(new Date(data.data.leave.startDate), 'dd/MM/yyyy')}</span>
                </div>
            )
        },
        {
            label: 'End Date',
            value: 'leave.endDate',
            isTime: true,
            render: (data: any) => (
                <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <span>{format(new Date(data.data.leave.endDate), 'dd/MM/yyyy')}</span>
                </div>
            )
        },
        {
            label: 'Reason',
            value: 'leave.reason',
            render: (data: any) => (
                <div className="max-w-[300px] truncate" title={data.data.leave.reason}>
                    {data.data.leave.reason}
                </div>
            )
        },
        {
            label: 'Status',
            value: 'leave.status',
            render: (data: any) => (
                <Badge className={getStatusColor(data.data.leave.status)}>
                    {getStatusText(data.data.leave.status)}
                </Badge>
            )
        },
    ];

    const UpdateComponent = ({ data }: { data: any }) => {
        if (data.leave.status === 'Pending') {
            return (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenModal(data.leave._id)}
                    className="hover:bg-blue-50 hover:text-blue-600 transition-colors"
                >
                    Process
                </Button>
            );
        }
        return null;
    };

    const months = [
        { value: 'all', label: 'All' },
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
    ];

    return (
        <div className="p-6">
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold">Leave Management</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium">Filter:</span>
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="Pending">Pending</SelectItem>
                                <SelectItem value="Approved">Approved</SelectItem>
                                <SelectItem value="Rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={month} onValueChange={setMonth}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Month" />
                            </SelectTrigger>
                            <SelectContent>
                                {months.map((m) => (
                                    <SelectItem key={m.value} value={m.value}>
                                        {m.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={userId} onValueChange={setUserId}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Employee" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {usersData?.users?.map((user: any) => (
                                    <SelectItem key={user._id} value={user._id}>
                                        {user.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    <TableCustomer
                        columns={columns}
                        data={data?.data || []}
                        loading={isLoading}
                        Update={UpdateComponent}
                        pagination={{
                            currentPage: page,
                            totalPages: Math.ceil((data?.total || 0) / 10),
                            totalItems: data?.total || 0,
                            itemsPerPage: 10,
                            onPageChange: setPage,
                        }}
                    />
                </CardContent>
            </Card>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="bg-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold">Process Leave Request</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-6 py-4">
                        <div className="grid gap-2">
                            <Label className="text-sm font-medium">Status</Label>
                            <Select value={selectedStatus} onValueChange={(value: string) => setSelectedStatus(value as 'Approved' | 'Rejected')}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Approved">Approved</SelectItem>
                                    <SelectItem value="Rejected">Rejected</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="adminNote" className="text-sm font-medium">Admin Note</Label>
                            <Textarea
                                id="adminNote"
                                value={adminNote}
                                onChange={(e) => setAdminNote(e.target.value)}
                                placeholder="Enter admin note..."
                                className="min-h-[120px] resize-none"
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setIsModalOpen(false)}
                            className="hover:bg-gray-100"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmUpdate}
                            disabled={!adminNote.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            Process
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Leaves;